import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';


// Types
export interface ConditionalLogic {
  id: number;
  name: string;
  conditions: any;
  actions: any;
  is_active: boolean;
  priority: number;
  form_page_id?: number;
  project_id: number;
  organization_id: number;
  connected_node_ids?: string[];
}

export interface LogicTemplate {
  id: number;
  name: string;
  description: string;
  logic_data: any;
  is_global: boolean;
  project_id?: number;
  organization_id: number;
  user_id: number;
  created_at?: string;
  updated_at?: string;
}

interface LogicState {
  conditionalLogics: ConditionalLogic[];
  templates: LogicTemplate[];
  currentLogic: ConditionalLogic | null;
  currentTemplate: LogicTemplate | null;
  isLoading: boolean;
  isLoadingOnUpdate: boolean;
  error: string | null;
}

// Add debounce timer for batch processing
let saveTemplateTimer: NodeJS.Timeout | null = null;
let latestSaveRequest: { projectIdOrSlug: string | number; data: Partial<LogicTemplate> } | null = null;

// Thunks
export const fetchConditionalLogics = createAsyncThunk(
  'logic/fetchConditionalLogics',
  async (projectIdOrSlug: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/conditional-logics`
      );
      return response.data.conditional_logics;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An error occurred');
    }
  }
);

export const fetchConditionalLogic = createAsyncThunk(
  'logic/fetchConditionalLogic',
  async (
    { projectIdOrSlug, id }: { projectIdOrSlug: string | number; id: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/conditional-logics/${id}`
      );
      return response.data.conditional_logic;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const createConditionalLogic = createAsyncThunk(
  'logic/createConditionalLogic',
  async (
    {
      projectIdOrSlug,
      data,
    }: {
      projectIdOrSlug: string | number;
      data: Partial<ConditionalLogic>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/conditional-logics`,
        data
      );
      return response.data.conditional_logic;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateConditionalLogic = createAsyncThunk(
  'logic/updateConditionalLogic',
  async (
    {
      projectIdOrSlug,
      id,
      data,
    }: {
      projectIdOrSlug: string | number;
      id: number;
      data: Partial<ConditionalLogic>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(
        `/projects/${projectIdOrSlug}/conditional-logics/${id}`,
        data
      );
      return response.data.conditional_logic;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const deleteConditionalLogic = createAsyncThunk(
  'logic/deleteConditionalLogic',
  async (
    { projectIdOrSlug, id }: { projectIdOrSlug: string | number; id: number },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.delete(
        `/projects/${projectIdOrSlug}/conditional-logics/${id}`
      );
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchOrganizationLogicTemplates = createAsyncThunk(
  'logic/fetchOrganizationLogicTemplates',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/organizations/${organizationId}/logic-templates`
      );
      if (!response.data.success) {
        return rejectWithValue('Failed to fetch organization templates');
      }
      return response.data.templates;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An error occurred');
    }
  }
);

export const getProjectLogicTemplate = createAsyncThunk(
  'logic/getProjectLogicTemplate',
  async (projectIdOrSlug: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/logic-template`
      );
      if (!response.data.success) {
        return rejectWithValue('Failed to fetch project template');
      }
      return response.data.template;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An error occurred');
    }
  }
);


export const saveLogicTemplate = createAsyncThunk(
  'logic/saveLogicTemplate',
  async (
    {
      projectIdOrSlug,
      data,
    }: {
      projectIdOrSlug: string | number;
      data: Partial<LogicTemplate>;
    },
    { rejectWithValue }
  ) => {
    try {
      // Clear any existing timer
      if (saveTemplateTimer) {
        clearTimeout(saveTemplateTimer);
      }
      
      // Store the latest request
      latestSaveRequest = { projectIdOrSlug, data };
      
      // Return a promise that will be resolved after the debounce period
      return await new Promise<LogicTemplate>((resolve, reject) => {
        saveTemplateTimer = setTimeout(async () => {
          try {
            // Only make the API call after 3 seconds of inactivity
            const response = await axiosInstance.post(
              `/projects/${latestSaveRequest!.projectIdOrSlug}/logic-template`,
              latestSaveRequest!.data
            );
            
            if (!response.data.success) {
              reject('Failed to save template');
              return;
            }
            
            // Resolve with the template data
            resolve(response.data.template);
          } catch (error: any) {
            reject(error.response?.data?.error || 'An error occurred');
          } finally {
            saveTemplateTimer = null;
            latestSaveRequest = null;
          }
        }, 50); // 1.2 second debounce
      });
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An error occurred');
    }
  }
);

// Add new processLogicInputs thunk
export const refineConditionsDefined = createAsyncThunk(
  'logic/processLogicInputs',
  async (
    {
      projectIdOrSlug,
      nodeConnections,
    }: {
      projectIdOrSlug: string | number;
      nodeConnections?: {[conditionId: string]: string[]};
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = nodeConnections ? { nodeConnections } : {};
      
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/refine-conditions-defined`,
        payload
      );
      if (!response.data.success) {
        return rejectWithValue('Failed to process logic inputs');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An error occurred');
    }
  }
);

export const deleteLogicTemplate = createAsyncThunk(
  'logic/deleteLogicTemplate',
  async (projectIdOrSlug: string | number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/projects/${projectIdOrSlug}/logic-template`);
      if (!response.data.success) {
        return rejectWithValue('Failed to delete template');
      }
      // Return project ID for the reducer to filter if needed
      return projectIdOrSlug;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An error occurred');
    }
  }
);

export const applyLogicTemplate = createAsyncThunk(
  'logic/applyLogicTemplate',
  async (
    projectIdOrSlug: string | number,
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/apply-logic-template`
      );
      if (!response.data.success) {
        return rejectWithValue('Failed to apply template');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'An error occurred');
    }
  }
);

// Slice
const logicSlice = createSlice({
  name: 'logic',
  initialState: {
    conditionalLogics: [],
    templates: [],
    currentLogic: null,
    currentTemplate: null,
    isLoading: false,
    error: null,
    isLoadingOnUpdate: false,
  } as LogicState,
  reducers: {
    clearLogicError: (state) => {
      state.error = null;
    },
    setCurrentLogic: (state, action: PayloadAction<ConditionalLogic | null>) => {
      state.currentLogic = action.payload;
    },
    setCurrentTemplate: (state, action: PayloadAction<LogicTemplate | null>) => {
      state.currentTemplate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch conditional logics
      .addCase(fetchConditionalLogics.pending, (state) => {
       // state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConditionalLogics.fulfilled, (state, action) => {
      //  state.isLoading = false;
        state.conditionalLogics = action.payload;
      })
      .addCase(fetchConditionalLogics.rejected, (state, action) => {
        // state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch single conditional logic
      .addCase(fetchConditionalLogic.pending, (state) => {
        // state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConditionalLogic.fulfilled, (state, action) => {
        // state.isLoading = false;
        state.currentLogic = action.payload;
      })
      .addCase(fetchConditionalLogic.rejected, (state, action) => {
        // state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create conditional logic
      .addCase(createConditionalLogic.pending, (state) => {
        // state.isLoading = true;
        state.error = null;
      })
      .addCase(createConditionalLogic.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conditionalLogics = [...state.conditionalLogics, action.payload];
        state.currentLogic = action.payload;
      })
      .addCase(createConditionalLogic.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update conditional logic
      .addCase(updateConditionalLogic.pending, (state) => {
        // state.isLoadingOnUpdate = true;
        state.error = null;
      })
      .addCase(updateConditionalLogic.fulfilled, (state, action) => {
        state.isLoadingOnUpdate = false;
        state.conditionalLogics = state.conditionalLogics.map(logic =>
          logic.id === action.payload.id ? action.payload : logic
        );
        state.currentLogic = action.payload;
      })
      .addCase(updateConditionalLogic.rejected, (state, action) => {
        state.isLoadingOnUpdate = false;
        state.error = action.payload as string;
      })
      
      // Delete conditional logic
      .addCase(deleteConditionalLogic.pending, (state) => {
        // state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteConditionalLogic.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conditionalLogics = state.conditionalLogics.filter(
          logic => logic.id !== action.payload
        );
        if (state.currentLogic && state.currentLogic.id === action.payload) {
          state.currentLogic = null;
        }
      })
      .addCase(deleteConditionalLogic.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch organization logic templates
      .addCase(fetchOrganizationLogicTemplates.pending, (state) => {
        // state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationLogicTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = action.payload;
      })
      .addCase(fetchOrganizationLogicTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Get project logic template
      .addCase(getProjectLogicTemplate.pending, (state) => {
        // state.isLoading = true;
        state.error = null;
      })
      .addCase(getProjectLogicTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTemplate = action.payload;
        
        // Check if this template already exists in our state
        const templateExists = state.templates.some(t => t.id === action.payload.id);
        if (!templateExists) {
          state.templates = [...state.templates, action.payload];
        } else {
          // Update the existing template
          state.templates = state.templates.map(template => 
            template.id === action.payload.id ? action.payload : template
          );
        }
      })
      .addCase(getProjectLogicTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Save logic template (create or update)
      .addCase(saveLogicTemplate.pending, (state) => {
      
        state.error = null;
      })
      .addCase(saveLogicTemplate.fulfilled, (state, action) => {
        // state.isLoading = false;
        state.currentTemplate = action.payload;
        
        // Check if this template already exists in our state
        const existingTemplateIndex = state.templates.findIndex(t => t.id === action.payload.id);
        
        if (existingTemplateIndex >= 0) {
          // Update existing template
          state.templates = state.templates.map(template =>
            template.id === action.payload.id ? action.payload : template
          );
        } else {
          // Add new template
          state.templates = [...state.templates, action.payload];
        }
      })
      .addCase(saveLogicTemplate.rejected, (state, action) => {
       // state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete logic template
      .addCase(deleteLogicTemplate.pending, (state) => {
       // state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLogicTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove all templates associated with the project
        // If we had the specific template ID, we could filter more precisely
        state.templates = state.templates.filter(
          template => template.project_id !== action.payload
        );
        // Reset current template if it belongs to the deleted project
        if (state.currentTemplate && state.currentTemplate.project_id === action.payload) {
          state.currentTemplate = null;
        }
      })
      .addCase(deleteLogicTemplate.rejected, (state, action) => {
       // state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Apply logic template
      .addCase(applyLogicTemplate.pending, (state) => {
       // state.isLoading = true;
        state.error = null;
      })
      .addCase(applyLogicTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        // If the response includes new conditional logics, update them
        if (action.payload.conditional_logics) {
          state.conditionalLogics = action.payload.conditional_logics;
        }
      })
      .addCase(applyLogicTemplate.rejected, (state, action) => {
       // state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Process logic inputs
      .addCase(refineConditionsDefined.pending, (state) => {
        // state.isLoading = true;
        state.error = null;
      })
      .addCase(refineConditionsDefined.fulfilled, (state, action) => {
        state.isLoading = false;
        // If the response includes new conditional logics, update them
        if (action.payload.conditional_logics) {
          state.conditionalLogics = action.payload.conditional_logics;
        }
      })
      .addCase(refineConditionsDefined.rejected, (state, action) => {
       // state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearLogicError, setCurrentLogic, setCurrentTemplate } = logicSlice.actions;
export default logicSlice.reducer; 